/* eslint-disable react/prop-types */
/* eslint-disable no-nested-ternary */

import React, { useCallback, useMemo, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { Col, Row } from "react-bootstrap";
import { List, CellMeasurerCache, CellMeasurer } from "react-virtualized";
import { isArray, orderBy, isEmpty } from "lodash";
import TextareaAutosize from "react-textarea-autosize";
import {
  IoFlagOutline,
  IoFlag,
  IoDocumentText,
  IoEye,
  IoPaperPlane,
  IoChevronUpOutline,
  IoChevronDownOutline
} from "react-icons/io5";

import Checkbox from "../common/Checkbox";
// import Loading from "../common/Loading";
import TasksServiceCallContainer from "./TasksServiceCallContainer";
import ClickableTooltip from "../common/ClickableTooltip";
import {
  customToast as toast,
  cleanHTMLString,
  generateUniqueId
} from "../../utils";

import { formatDate, addHoursToDate } from "../../helpers/date";

import CallApprovalProgressBar from "./CallApprovalProgressBar";

const CallApprovalList = ({
  calls,
  codes,
  updateCall,
  loading,
  auth,
  services,
  projects,
  setNeedUpdate,
  highLevelApprove,
  onSortCalls,
  sortBy,
  customers,
  users,
  openEmailNotification,
  filters,
  reviewType
}) => {
  const groupedByCustomer = useMemo(
    () => !!filters.grouped_by_customer,
    [filters]
  );
  const callColumns = [
    {
      name: "projectObj",
      label: "Project",
      size: 2
    },
    {
      name: "serviceObj",
      label: "Service",
      size: 2
    },
    {
      name: "callType",
      label: "Type",
      size: 2
    },
    {
      name: "arrival_date",
      label: "Date",
      size: 2
    },
    {
      name: "actual_hrs",
      label: "Actual",
      size: 1
    },
    {
      name: "billable_hrs",
      label: "Billable",
      size: 1
    }
  ];

  const listRef = useRef(null);

  const cache = new CellMeasurerCache({
    fixedWidth: true,
    minHeight: 50
  });

  const onChange = (call) => async (e) => {
    if (e.target.name === "approved") {
      call.approved = e.target.checked;
      call.approved_changed = true;
    } else {
      setNeedUpdate(true);
    }
    if (e.target.name === "customer") {
      call.customer = e.target.value;
      call.customer_changed = true;
    }

    if (e.target.name === "assigned_to") {
      call.assigned_to = e.target.value;
      call.assigned_to_changed = true;
    }

    if (
      e.target.name === "is_flagged" ||
      e.currentTarget.name === "is_flagged"
    ) {
      call.is_flagged = !call.is_flagged;
      call.is_flagged_changed = true;

      call.approved = call.approved && call.is_flagged ? false : call.approved;
      call.approved_changed = true;
    }

    if (e.target.name === "fixed_time") {
      call.fixed_time = e.target.checked;
      call.fixed_time_changed = true;
    }

    if (e.target.name === "off_hours") {
      call.off_hours = e.target.checked;
      call.off_hours_changed = true;
    }

    if (e.target.name === "service") {
      call.service = e.target.value;
      call.service_changed = true;
    }

    if (
      e.target.name === "internal_notes" &&
      call.internal_notes !== e.target.value
    ) {
      call.internal_notes = e.target.value;
      call.internal_notes_changed = true;
    }

    if (
      e.target.name === "invoice_notes" &&
      call.invoice_notes !== e.target.value
    ) {
      call.invoice_notes = e.target.value;
      call.invoice_notes_changed = true;
    }

    if (
      e.target.name === "billing_description" &&
      call.billing_description !== e.target.value
    ) {
      call.billing_description = e.target.value;
      call.billing_description_changed = true;
    }

    if (
      e.target.name === "review_notes" &&
      call.review_notes !== e.target.value
    ) {
      call.review_notes = e.target.value;
      call.review_notes_changed = true;
    }

    if (e.target.name === "call_type") {
      const callType = codes.find((code) => code._id === e.target.value);
      const { is_billable } = callType;
      if (!is_billable && call.billable_hrs !== 0) {
        call.billable_hrs = 0;
        call.billable_hrs_changed = true;
      } else if (is_billable && call.billable_hrs !== call.actual_hrs) {
        call.billable_hrs = call.actual_hrs;
        call.billable_hrs_changed = true;
      }
      call.call_type = e.target.value;
      call.call_type_changed = true;
      call.is_billable = [is_billable];
    }

    if (e.target.name === "actual_hrs") {
      let newValue = 0;
      if (~~parseFloat(e.target.value) - parseFloat(e.target.value) === 0) {
        newValue = parseFloat(e.target.value);
      } else {
        newValue =
          ~~parseFloat(e.target.value) - parseFloat(e.target.value) < 0
            ? ~~parseFloat(e.target.value) + 0.5
            : parseFloat(e.target.value);
      }
      call.actual_hrs = newValue;
      call.actual_hrs_changed = true;

      const arrivalDate = call.arrival_date;
      call.completed_date = addHoursToDate(arrivalDate, newValue);
      call.completed_date_changed = true;
    }

    if (e.target.name === "billable_hrs") {
      let newValue = 0;
      if (~~parseFloat(e.target.value) - parseFloat(e.target.value) === 0) {
        newValue = parseFloat(e.target.value);
      } else {
        newValue =
          ~~parseFloat(e.target.value) - parseFloat(e.target.value) < 0
            ? ~~parseFloat(e.target.value) + 0.5
            : parseFloat(e.target.value);
      }
      call.billable_hrs = newValue;
      call.billable_hrs_changed = true;
    }

    if (e.target.name === "project") {
      const project = projects.find((p) => p._id === e.target.value);
      call.project = e.target.value;
      call.project_changed = true;
      // update contract from project
      if (!project.contract) {
        call.contract = null;
        call.contract_changed = true;
      } else if (call.contract !== project.contract._id) {
        call.contract = project.contract?._id;
        call.contract_changed = true;
      }

      // update call type from project and hours from call type
      const callType = codes.find((code) => code._id === project.call_type);
      const { is_billable } = callType;
      if (!is_billable && call.billable_hrs !== 0) {
        call.billable_hrs = 0;
        call.billable_hrs_changed = 0;
      } else if (is_billable && call.billable_hrs !== call.actual_hrs) {
        call.billable_hrs = call.actual_hrs;
        call.billable_hrs_changed = true;
      }
      if (call.call_type !== callType._id) {
        call.call_type = callType._id;
        call.callType = callType;
        call.is_billable = [is_billable];
        call.call_type_changed = true;
      }
    }
    await updateCall(call);
  };

  const billableType = codes?.find(
    (c) => c.label === "BILLABLE" && c.entity === "Call" && c.field === "type"
  );

  // if call type is_billable, check contract to determine billable_hrs
  const calculateBillableHours = (call, actual_hrs) => {
    let billable_hrs;
    switch (call.callType.value) {
      case "FIXED FEE":
      case "RETAINER":
      case "CLIENTCARE":
      case "CAPITAL":
      case "MANAGED SERVICES":
        billable_hrs = actual_hrs;
        break;
      default:
        billable_hrs = Math.ceil(actual_hrs);
        break;
    }
    return billable_hrs;
  };

  const handleTasksChange = (name, value, task, call, service) => {
    const data = {
      ...task,
      [name]: name === "actual_duration" ? parseFloat(value, 10) : value
    };

    if (service) {
      data.service = service;
    }
    const newTasks = call.tasks.map((_task) =>
      _task._id === data._id ? data : _task
    );
    let sumOfHours = 0;
    newTasks.forEach((_task) => {
      let valueToSum = Number(_task.actual_duration);
      if (_task.duration_units === "Minutes") {
        valueToSum = Number(_task.actual_duration) / 60;
      }
      sumOfHours += valueToSum;
    });
    if (sumOfHours > 24) {
      toast.error("Calls may not exceed 24 hours");
    }
    if (call.callType?.is_billable) {
      call.billable_hrs = calculateBillableHours(
        call,
        parseFloat(sumOfHours.toFixed(2), 10)
      );
    }
    call.tasks_changed = true;
    updateCall({
      ...call,
      actual_hrs: parseFloat(sumOfHours.toFixed(2), 10),
      tasks: newTasks
    });
    setNeedUpdate(true);
  };

  const servicesOptions = useCallback(
    (idx) => {
      const call = calls[idx];
      const assignedTo = users.find((u) => u._id === call.assigned_to);

      const GraVoc = customers.find(
        (customer) => customer.number === "GRAVOCAS01"
      );
      const personalProject = projects.find(
        (project) => project.project_number === "GRAVOCASSO-0076"
      );
      const personalServices = new Set([961, 985, 991, 992, 993, 994]);
      const serviceOptions = services
        .filter((service) => {
          if (service.status) {
            // if personal time, return only personal services
            if (
              personalProject &&
              call.project &&
              call.project === personalProject._id
            ) {
              if (personalServices.has(service.code)) {
                return service;
              }
            }
            // if customer is GraVoc and project is not personal, return all services except perosnal ones
            else if (GraVoc && call.customer === GraVoc._id) {
              if (!personalServices.has(service.code)) {
                return service;
              }
            }
            // otherwise match assignedTo department with service department
            // OR service with all department === true
            else if (
              assignedTo?.department?._id === service?.department?._id ||
              service?.all_departments
            ) {
              return service;
            }
            return false;
          }
          return false;
        })
        .map((service) => ({ value: service._id, label: service.description }))
        .sort((a, b) => {
          const x = a.label.toLowerCase();
          const y = b.label.toLowerCase();
          if (x < y) {
            return -1;
          }
          if (x > y) {
            return 1;
          }
          return 0;
        });
      return serviceOptions;
    },
    [calls, customers, projects, services, users]
  );

  useEffect(() => {
    if (!isEmpty(sortBy)) {
      cache.clearAll();
      listRef.current.recomputeRowHeights();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy]);

  const rowRenderer = ({ index, key, parent, style }) => {
    const call = calls[index];
    return (
      <CellMeasurer
        cache={cache}
        columnIndex={0}
        key={key}
        rowIndex={index}
        parent={parent}
      >
        {({ registerChild }) => (
          <div
            ref={registerChild}
            key={call._id}
            className="list-item"
            disabled={loading}
            style={style}
          >
            <div className="approve-checkbox-wrapper" style={{ width: 20 }}>
              <Checkbox
                name="approved"
                onClickCheck={onChange(call)}
                checked={call.approved && !call.is_flagged}
                disabled={call.is_flagged || loading || false}
              />
            </div>
            <div className="list-item-wrapper">
              <div className={`top-options ${call.approved ? "changed" : ""}`}>
                <Col xs={2}>
                  {highLevelApprove ? (
                    <select
                      name="assigned_to"
                      disabled
                      className={`table-select ${
                        call.assigned_to_changed ? "changed" : ""
                      }`}
                      onChange={onChange(call)}
                      style={
                        billableType._id === call.call_type && highLevelApprove
                          ? { color: "red" }
                          : null
                      }
                    >
                      <option selected>{call.user.full_name}</option>
                    </select>
                  ) : (
                    <React.Fragment>
                      {groupedByCustomer ? (
                        <select
                          name="user"
                          disabled
                          className={`table-select ${
                            auth.isNightMode ? "dark-select" : ""
                          }`}
                          style={
                            billableType._id === call.call_type &&
                            highLevelApprove
                              ? { color: "red" }
                              : null
                          }
                        >
                          <option selected>{call.user.full_name}</option>
                        </select>
                      ) : (
                        <select
                          name="customer"
                          disabled
                          className={`table-select ${
                            call.customer_changed
                              ? auth.isNightMode
                                ? "changed-dark"
                                : "changed-light"
                              : auth.isNightMode
                                ? "dark-select"
                                : ""
                          }`}
                          style={
                            billableType._id === call.call_type &&
                            highLevelApprove
                              ? { color: "red" }
                              : null
                          }
                          onChange={onChange(call)}
                        >
                          <option selected>{call.customerObj.name}</option>
                        </select>
                      )}
                    </React.Fragment>
                  )}
                </Col>
                <Col xs={2}>
                  <select
                    name="project"
                    disabled={loading}
                    className={`table-select ${
                      call.project_changed
                        ? auth.isNightMode
                          ? "changed-dark"
                          : "changed-light"
                        : auth.isNightMode
                          ? "dark-select"
                          : ""
                    }`}
                    style={
                      billableType._id === call.call_type && highLevelApprove
                        ? { color: "red" }
                        : null
                    }
                    onChange={onChange(call)}
                  >
                    {projects &&
                    Array.isArray(projects) &&
                    projects.length > 0 ? (
                      orderBy(projects, "name")
                        .filter(
                          (project) =>
                            project.customer &&
                            call.customer === project.customer &&
                            (project.project_status.label === "In Process" ||
                              project.project_status.label === "Ready To Close")
                        )
                        .map((project) => (
                          <option
                            key={project._id}
                            value={project._id}
                            selected={call.project === project._id}
                          >
                            {project.name}
                          </option>
                        ))
                    ) : (
                      <p>Loading...</p>
                    )}
                  </select>
                </Col>
                <Col xs={2}>
                  <select
                    name="service"
                    disabled={loading}
                    className={`table-select ${
                      call.service_changed
                        ? auth.isNightMode
                          ? "changed-dark"
                          : "changed-light"
                        : auth.isNightMode
                          ? "dark-select"
                          : ""
                    }`}
                    style={
                      billableType._id === call.call_type && highLevelApprove
                        ? { color: "red" }
                        : null
                    }
                    onChange={onChange(call)}
                  >
                    {/* {services &&
                      orderBy(services, "description").map(service => (
                        <option
                          key={service._id}
                          value={service._id}
                          selected={call.service === service._id}
                        >
                          {service.description}
                        </option>
                      ))} */}
                    {servicesOptions(index).map((service) => (
                      <option
                        key={service.value}
                        value={service.value}
                        selected={call.service === service.value}
                      >
                        {service.label}
                      </option>
                    ))}
                  </select>
                </Col>
                <Col xs={2}>
                  <select
                    name="call_type"
                    disabled
                    className={`table-select ${
                      call.call_type_changed
                        ? auth.isNightMode
                          ? "changed-dark"
                          : "changed-light"
                        : auth.isNightMode
                          ? "dark-select"
                          : ""
                    }`}
                    style={
                      billableType._id === call.call_type && highLevelApprove
                        ? { color: "red" }
                        : null
                    }
                    onChange={onChange(call)}
                  >
                    <option selected>{call.callType?.label}</option>
                  </select>
                </Col>
                <Col xs={2}>
                  <span
                    className="date"
                    style={
                      billableType._id === call.call_type && highLevelApprove
                        ? { color: "red" }
                        : null
                    }
                  >
                    {formatDate(call.arrival_date, "MM/dd/yy")}
                  </span>
                </Col>
                <Col className="actual" xs={1}>
                  <input
                    disabled
                    name="actual_hrs"
                    type="number"
                    step=".5"
                    style={
                      billableType._id === call.call_type && highLevelApprove
                        ? { color: "red" }
                        : null
                    }
                    value={call.actual_hrs}
                    className={`review-input number ${
                      call.actual_hrs_changed
                        ? auth.isNightMode
                          ? "changed-dark"
                          : "changed-light"
                        : auth.isNightMode
                          ? "dark-select"
                          : ""
                    }`}
                    onChange={onChange(call)}
                  />
                </Col>
                <Col className="billable" xs={1}>
                  <input
                    name="billable_hrs"
                    type="number"
                    step=".5"
                    style={
                      billableType._id === call.call_type && highLevelApprove
                        ? { color: "red" }
                        : null
                    }
                    value={call.billable_hrs}
                    disabled={!call.callType?.is_billable || loading}
                    className={`review-input number ${
                      call.billable_hrs_changed
                        ? auth.isNightMode
                          ? "changed-dark"
                          : "changed-light"
                        : auth.isNightMode
                          ? "dark-select"
                          : ""
                    }`}
                    onChange={onChange(call)}
                  />
                </Col>
                <Col xs={1}>
                  <div
                    className={`review-input ${
                      call.fixed_time_changed ? "changed" : ""
                    }`}
                  >
                    <input
                      className="checkbox"
                      type="checkbox"
                      name="fixed_time"
                      onClick={onChange(call)}
                      value={call.fixed_time}
                      checked={call.fixed_time}
                      color="red"
                    />
                  </div>
                </Col>
                <Col xs={1}>
                  <div
                    className={`review-input ${
                      call.off_hours_changed ? "changed" : ""
                    }`}
                  >
                    <input
                      className="checkbox"
                      type="checkbox"
                      name="off_hours"
                      onClick={onChange(call)}
                      value={call.off_hours}
                      checked={call.off_hours}
                      color="red"
                    />
                  </div>
                </Col>
              </div>
              {isArray(call.tasks) && (
                <div>
                  <Row style={{ margin: 0 }}>
                    <Col xs={12}>
                      {call.tasks.map((task, i) => (
                        <TasksServiceCallContainer
                          key={generateUniqueId()}
                          auth={auth}
                          serviceId={call.service}
                          services={services}
                          values={task}
                          onChange={(name, value, service = null) =>
                            handleTasksChange(name, value, task, call, service)
                          }
                          reviewType={reviewType}
                          taxCode={call.serviceObj?.taxCode?.code}
                          index={i}
                          tasksLength={call.tasks.length}
                        />
                      ))}
                    </Col>
                  </Row>
                </div>
              )}
              <div className="notes">
                <Row>
                  <Col
                    xs={highLevelApprove ? 3 : 4}
                    style={{
                      display: "inline"
                    }}
                  >
                    <strong>Internal: &nbsp;</strong>
                    <TextareaAutosize
                      disabled={loading}
                      name="internal_notes"
                      type="textarea"
                      value={cleanHTMLString(call.internal_notes)}
                      className={`review-input ${
                        call.internal_notes_changed
                          ? auth.isNightMode
                            ? "changed-dark"
                            : "changed-light"
                          : ""
                      }`}
                      onChange={onChange(call)}
                    />
                    {call.internal_notes &&
                      call.internal_notes.length > 500 && (
                        <p className="error-message">Maximum 500 characters</p>
                      )}
                  </Col>
                  <Col
                    xs={highLevelApprove ? 3 : 4}
                    style={{ display: "inline" }}
                  >
                    <strong>Billing: &nbsp;&nbsp;&nbsp;</strong>
                    <TextareaAutosize
                      disabled={loading}
                      name="billing_description"
                      type="textarea"
                      value={cleanHTMLString(call.billing_description)}
                      className={`review-input ${
                        call.billing_description_changed
                          ? auth.isNightMode
                            ? "changed-dark"
                            : "changed-light"
                          : ""
                      }`}
                      onChange={onChange(call)}
                    />
                    {call.billing_description &&
                      call.billing_description.length > 100 && (
                        <span className="error-message">
                          Maximum 100 characters
                        </span>
                      )}
                  </Col>
                  <Col
                    xs={highLevelApprove ? 3 : 4}
                    style={{ display: "inline" }}
                  >
                    <strong>Review Note: &nbsp;</strong>
                    <TextareaAutosize
                      disabled={loading}
                      name="review_notes"
                      type="textarea"
                      value={cleanHTMLString(call.review_notes)}
                      className={`review-input ${
                        call.review_notes_changed
                          ? auth.isNightMode
                            ? "changed-dark"
                            : "changed-light"
                          : ""
                      }`}
                      onChange={onChange(call)}
                    />
                    {call.review_notes && call.review_notes.length > 500 && (
                      <span className="error-message">
                        Maximum 500 characters
                      </span>
                    )}
                  </Col>
                  {highLevelApprove && (
                    <Col xs={3} style={{ display: "inline" }}>
                      <strong>Invoice Note: &nbsp;</strong>
                      <TextareaAutosize
                        disabled={loading}
                        name="invoice_notes"
                        type="textarea"
                        value={cleanHTMLString(call.invoice_notes)}
                        className={`review-input ${
                          call.invoice_notes_changed
                            ? auth.isNightMode
                              ? "changed-dark"
                              : "changed-light"
                            : ""
                        }`}
                        onChange={onChange(call)}
                      />
                      {call.invoice_notes &&
                        call.invoice_notes.length > 500 && (
                          <span className="error-message">
                            Maximum 500 characters
                          </span>
                        )}
                    </Col>
                  )}
                </Row>
              </div>
            </div>
            <div
              className="approve-checkbox-wrapper last-options"
              style={{ width: 35, padding: 0 }}
            >
              {call.contractObj &&
                call.contractObj.service_note &&
                call.contractObj.service_note !== "" && (
                  <ClickableTooltip
                    id={`service-note-${call._id}`}
                    isNightMode={auth.isNightMode}
                    loading={loading}
                    containerStyle={{
                      paddingLeft: 5,
                      paddingBottom: 5
                    }}
                    text={call.contractObj.service_note}
                  >
                    <IoDocumentText style={{ color: "#50accb" }} />
                  </ClickableTooltip>
                )}
              {Array.isArray(projects) && projects.length > 0 && (
                <CallApprovalProgressBar
                  project={projects.find(
                    (project) => project._id === call.projectObj?._id
                  )}
                  auth={auth}
                  call={call}
                />
              )}
              <button
                style={{
                  border: "none",
                  background: "none"
                }}
                disabled={loading}
                onClick={onChange(call)}
                name="is_flagged"
              >
                {call.is_flagged ? (
                  <IoFlag
                    style={{
                      color: "#fe703f"
                    }}
                  />
                ) : (
                  <IoFlagOutline
                    style={{
                      color: auth.isNightMode ? "#fff" : "#373737"
                    }}
                  />
                )}
              </button>
              <button
                disabled={loading}
                style={{
                  border: "none",
                  background: "none"
                }}
                type="button"
                onClick={() => openEmailNotification(call)}
              >
                <IoPaperPlane
                  style={{
                    color: auth.isNightMode ? "#fff" : "#373737"
                  }}
                />
              </button>
              <a
                href={`/app/calls/${call._id}`}
                className="eye-icon projectIcon"
                rel="noopener noreferrer"
                style={{
                  color: auth.isNightMode ? "#b8bac5" : "#373737"
                }}
                target="_blank"
              >
                <IoEye />
              </a>
            </div>
          </div>
        )}
      </CellMeasurer>
    );
  };

  return (
    <div className="content-list">
      <div className="header-titles">
        <Col xs={2}>
          <p
            style={{ cursor: "pointer" }}
            role="presentation"
            onKeyDown={() => {}}
            onClick={() => {
              onSortCalls(
                highLevelApprove || groupedByCustomer ? "user" : "customerObj"
              );
            }}
          >
            {highLevelApprove || groupedByCustomer ? "Employee" : "Customer"}
            &nbsp;
            {(((highLevelApprove || groupedByCustomer) && sortBy?.user) ||
              (!(highLevelApprove || groupedByCustomer) &&
                sortBy?.customerObj)) &&
              (sortBy?.customerObj === "asc" || sortBy?.user === "asc" ? (
                <IoChevronUpOutline
                  style={{ cursor: "pointer" }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onSortCalls(
                      highLevelApprove || groupedByCustomer
                        ? "user"
                        : "customerObj"
                    );
                  }}
                />
              ) : (
                <IoChevronDownOutline
                  style={{ cursor: "pointer" }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onSortCalls(
                      highLevelApprove || groupedByCustomer
                        ? "user"
                        : "customerObj"
                    );
                  }}
                />
              ))}
          </p>
        </Col>
        {callColumns.map((column) => (
          <React.Fragment key={generateUniqueId()}>
            <Col xs={column.size}>
              <p
                style={{ cursor: "pointer" }}
                role="presentation"
                onKeyDown={() => {}}
                onClick={() => onSortCalls(column.name)}
              >
                {column.label}&nbsp;
                {sortBy?.[column.name] &&
                  (sortBy?.[column.name] === "asc" ? (
                    <IoChevronUpOutline
                      style={{ cursor: "pointer" }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onSortCalls(column.name);
                      }}
                    />
                  ) : (
                    <IoChevronDownOutline
                      style={{ cursor: "pointer" }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onSortCalls(column.name);
                      }}
                    />
                  ))}
              </p>
            </Col>
          </React.Fragment>
        ))}
        <Col xs={1}>
          <p>&nbsp;Fixed&nbsp;</p>
        </Col>
        <Col xs={1}>
          <p>&nbsp;Off Hours&nbsp;</p>
        </Col>
        {/* <Col xs={1}>
          {loading && (
            <div className="loading-loader-load-container">
              <Loading active spinner size={30} />
            </div>
          )}
        </Col> */}
      </div>
      <List
        data={calls}
        width={1}
        containerStyle={{
          width: "100%",
          maxWidth: "100%"
        }}
        style={{
          width: "100%"
        }}
        overscanRowCount={0}
        height={500}
        rowCount={calls.length}
        rowHeight={cache.rowHeight}
        rowRenderer={rowRenderer}
        ref={listRef}
      />
    </div>
  );
};

CallApprovalList.propTypes = {
  calls: PropTypes.array,
  projects: PropTypes.array,
  services: PropTypes.array,
  codes: PropTypes.array,
  user: PropTypes.object,
  auth: PropTypes.object,
  loading: PropTypes.bool,
  updateCall: PropTypes.func,
  setNeedUpdate: PropTypes.func,
  highLevelApprove: PropTypes.bool,
  onSortCalls: PropTypes.func,
  sortBy: PropTypes.object,
  customers: PropTypes.array,
  users: PropTypes.array,
  openEmailNotification: PropTypes.func,
  filters: PropTypes.object,
  reviewType: PropTypes.string
};

CallApprovalList.defaultProps = {
  loading: false,
  filters: {}
};

export default CallApprovalList;
